import { useMemo, useState } from "react";

const MONTHS = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
];

function pad2(n) {
    return String(n).padStart(2, "0");
}
function isValidDate(d) {
    return d instanceof Date && !isNaN(d.getTime());
}
function startOfDay(d) {
    const x = new Date(d);
    x.setHours(0, 0, 0, 0);
    return x;
}
function parseInput(value) {
    // Accept Date, ISO string, or "DD/MM/YYYY"
    if (!value) return null;
    if (value instanceof Date) return isValidDate(value) ? value : null;
    if (typeof value === "string") {
        // try ISO
        const iso = new Date(value);
        if (isValidDate(iso)) return iso;
        // try DD/MM/YYYY
        const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (m) {
            const d = new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1]));
            return isValidDate(d) ? d : null;
        }
    }
    return null;
}
function formatDDMMYYYY(d) {
    if (!isValidDate(d)) return "";
    return `${pad2(d.getDate())}/${pad2(d.getMonth() + 1)}/${d.getFullYear()}`;
}
function isSameDay(a, b) {
    if (!isValidDate(a) || !isValidDate(b)) return false;
    return (
        a.getFullYear() === b.getFullYear() &&
        a.getMonth() === b.getMonth() &&
        a.getDate() === b.getDate()
    );
}

export default function HodDesktopDatePicker({
    value,
    onChange,
    label,
    placeholder = "DD/MM/YYYY",
    minYear = 1900,
    maxYear = 2099,
    disablePast = false,
    disableFuture = false,
    className = "",
    inputClassName = "",
    dropdownClassName = "",
    error = false,
    helperText = "",
}) {
    const today = useMemo(() => startOfDay(new Date()), []);
    const minDate = useMemo(() => new Date(minYear, 0, 1), [minYear]);
    const maxDate = useMemo(() => new Date(maxYear, 11, 31), [maxYear]);

    const selected = parseInput(value);
    const initialView = selected ?? today;

    const [open, setOpen] = useState(false);
    const [viewYear, setViewYear] = useState(initialView.getFullYear());
    const [viewMonth, setViewMonth] = useState(initialView.getMonth());
    const [showYearList, setShowYearList] = useState(false);

    const firstDow = new Date(viewYear, viewMonth, 1).getDay();
    const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
    const years = Array.from(
        { length: maxYear - minYear + 1 },
        (_, i) => minYear + i
    );

    function dayDate(day) {
        return new Date(viewYear, viewMonth, day);
    }

    function isOutOfRange(date) {
        if (date < minDate || date > maxDate) return true;
        if (disablePast && date < today) return true;
        if (disableFuture && date > today) return true;
        return false;
    }

    function prevMonth() {
        const y = viewMonth === 0 ? viewYear - 1 : viewYear;
        const m = viewMonth === 0 ? 11 : viewMonth - 1;
        setViewYear(y);
        setViewMonth(m);
    }
    function nextMonth() {
        const y = viewMonth === 11 ? viewYear + 1 : viewYear;
        const m = viewMonth === 11 ? 0 : viewMonth + 1;
        setViewYear(y);
        setViewMonth(m);
    }

    function pick(day) {
        const d = dayDate(day);
        if (isOutOfRange(d)) return;
        onChange?.(d);
        setOpen(false);
        setShowYearList(false);
    }

    return (
        <div className={`relative w-full ${className}`}>
            {label && (
                <label className="block text-sm font-semibold mb-1 text-gray-700">
                    {label}
                </label>
            )}

            <div className="relative">
                <input
                    type="text"
                    readOnly
                    value={selected ? formatDDMMYYYY(selected) : ""}
                    placeholder={placeholder}
                    onClick={() => setOpen((o) => !o)}
                    className={`w-full border rounded-sm px-3 py-2 bg-white cursor-pointer focus:outline-hidden focus:ring-2 ${error
                        ? "border-red-500 focus:ring-red-200"
                        : "border-gray-300 focus:ring-blue-200"
                        } ${inputClassName}`}
                />
                {/* Calendar icon (optional) */}
                <svg
                    onClick={() => setOpen((o) => !o)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500 cursor-pointer"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                >
                    <rect x="3" y="4" width="18" height="18" rx="2" />
                    <line x1="16" y1="2" x2="16" y2="6" />
                    <line x1="8" y1="2" x2="8" y2="6" />
                    <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
            </div>

            {/* Error message */}
            {error && helperText && (
                <p className="mt-1 text-sm text-red-600">{helperText}</p>
            )}

            {open && (
                <>
                    {/* Backdrop to close without useEffect */}
                    <div
                        className="fixed inset-0 z-40"
                        onClick={() => {
                            setOpen(false);
                            setShowYearList(false);
                        }}
                    />
                    <div
                        className={`absolute z-50 mt-2 w-full rounded-xl border bg-white shadow-xl p-3 ${dropdownClassName}`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between px-1 py-2">
                            <button
                                type="button"
                                onClick={prevMonth}
                                className="px-2 py-1 rounded-xs hover:bg-gray-100"
                                aria-label="Previous month"
                            >
                                ◀
                            </button>

                            <button
                                type="button"
                                onClick={() => setShowYearList((v) => !v)}
                                className="font-medium px-2 py-1 rounded-xs hover:bg-gray-100"
                                aria-haspopup="listbox"
                                aria-expanded={showYearList}
                                title="Select year"
                            >
                                {MONTHS[viewMonth]} {viewYear}
                            </button>

                            <button
                                type="button"
                                onClick={nextMonth}
                                className="px-2 py-1 rounded-xs hover:bg-gray-100"
                                aria-label="Next month"
                            >
                                ▶
                            </button>
                        </div>

                        {/* Year dropdown */}
                        {showYearList && (
                            <div className="max-h-64 overflow-y-auto grid grid-cols-4 gap-2 p-2 border rounded-sm">
                                {years.map((y) => (
                                    <button
                                        key={y}
                                        type="button"
                                        onClick={() => {
                                            setViewYear(y);
                                            setShowYearList(false);
                                        }}
                                        className={`w-full text-left px-3 py-2 hover:bg-gray-100 ${y === viewYear
                                            ? "bg-primary text-white font-medium rounded-sm"
                                            : ""
                                            }`}
                                    >
                                        {y}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Week headers */}
                        <div className="grid grid-cols-7 text-center text-xs font-semibold text-gray-600 px-1 mt-1 mb-2">
                            {["S", "M", "T", "W", "T", "F", "S"].map((d) => (
                                <div key={d}>{d}</div>
                            ))}
                        </div>

                        {/* Days grid */}
                        <div className="grid grid-cols-7 gap-y-1 text-center px-1 pb-1">
                            {Array.from({ length: firstDow }).map((_, i) => (
                                <div key={`sp-${i}`} />
                            ))}
                            {Array.from(
                                { length: daysInMonth },
                                (_, i) => i + 1
                            ).map((day) => {
                                const d = dayDate(day);
                                const disabled = isOutOfRange(startOfDay(d));
                                const isSel =
                                    selected && isSameDay(selected, d);
                                const isToday = isSameDay(today, d);

                                return (
                                    <button
                                        key={day}
                                        type="button"
                                        onClick={() => pick(day)}
                                        disabled={disabled}
                                        className={`mx-auto w-9 h-9 rounded-full text-sm flex items-center justify-center ${disabled
                                            ? "text-gray-300 cursor-not-allowed"
                                            : "hover:bg-gray-100"
                                            } ${isToday && !isSel
                                                ? "ring-1 ring-blue-300"
                                                : ""
                                            } ${isSel
                                                ? "bg-blue-600 text-white hover:bg-blue-600"
                                                : ""
                                            }`}
                                    >
                                        {day}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
